import { distanceMetres } from '@blink/shared';
import { api } from '@/lib/api';
import { isNetworkError } from '@/lib/outbox';

/**
 * Location while on shift (P3-11): one point every 30 seconds, or sooner after 50 metres, sent in
 * batches. Runs only while a shift is open; nothing is taken off shift. In the browser it works
 * while the app is open; with the screen off, the Android app (step 12) will send its own points
 * with source BACKGROUND to the same endpoint.
 */

const EVERY_MS = 30_000;
const EVERY_METRES = 50;
/** points kept on the phone while there is no signal (about 8 hours at one every 30 s) */
const MAX_KEPT = 1000;

interface Point {
  at: string;
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
  speedMps: number | null;
}

let watchId: number | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let latest: GeolocationPosition | null = null;
let last: { at: number; latitude: number; longitude: number } | null = null;
let kept: Point[] = [];
let lastSent = 0;
let sending = false;

function take(pos: GeolocationPosition, at = Date.now()) {
  const here = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
  if (last && at - last.at < EVERY_MS && distanceMetres(last, here) < EVERY_METRES) return;
  last = { at, ...here };
  kept.push({ at: new Date(at).toISOString(), ...here, accuracyMeters: pos.coords.accuracy ?? null, speedMps: pos.coords.speed ?? null });
  if (kept.length > MAX_KEPT) kept = kept.slice(-MAX_KEPT);
}

async function send(force = false) {
  if (sending || !kept.length || (!force && Date.now() - lastSent < EVERY_MS)) return;
  sending = true;
  const batch = kept.splice(0, 500);
  try {
    const answer = await api<{ accepted: number; onShift: boolean }>('/courier/gps', { method: 'POST', body: { source: 'APP', points: batch } });
    lastSent = Date.now();
    // the shift was ended (or closed automatically): stop at once
    if (!answer.onShift) stopTracking();
  } catch (e) {
    // no signal: keep the points and try again on the next round; refused by the server: drop them
    if (isNetworkError(e)) kept = [...batch, ...kept].slice(-MAX_KEPT);
  } finally {
    sending = false;
  }
}

/** Starts taking points (shift open). Calling it again does nothing. */
export function startTracking() {
  if (watchId != null || !navigator.geolocation) return;
  lastSent = 0;
  watchId = navigator.geolocation.watchPosition(
    (pos) => {
      latest = pos;
      take(pos);
      // the first point goes at once, so the office sees the courier from the start
      void send(lastSent === 0);
    },
    () => undefined,
    { enableHighAccuracy: true, maximumAge: 10_000 },
  );
  // standing still: the phone reports no change, so repeat the last place every 30 s
  timer = setInterval(() => {
    if (latest) take(latest);
    void send();
  }, 10_000);
}

/** Stops taking points (shift ended, or signed out). Points not yet sent are dropped. */
export function stopTracking() {
  if (watchId != null) navigator.geolocation.clearWatch(watchId);
  if (timer) clearInterval(timer);
  watchId = null;
  timer = null;
  latest = null;
  last = null;
  kept = [];
}
